
import React from 'react';
import { Belt } from './timeline/types';
import { Card } from '@/components/ui/card';
import { X } from 'lucide-react';
import { MacroCurriculum, MesoCurriculum } from '@/services/roadmap/roadmapService';
import { SimpleMarkdown } from '@/components/admin/roadmap/SimpleMarkdown';

interface CurriculumDialogProps {
  belt: Belt | null;
  curriculum?: MacroCurriculum | MesoCurriculum | null;
  onClose: () => void;
}

export const CurriculumDialog: React.FC<CurriculumDialogProps> = ({ belt, curriculum, onClose }) => {
  if (!belt) return null;
  
  // Format the title based on whether this is a belt or a stripe
  const displayTitle = belt.isStripe 
    ? `${belt.name}` // Already formatted as "Belt rank X stripes" in the stripe component
    : `${belt.name} belt`;
  
  return (
    <Card className="p-4 mt-4 relative border shadow-md animate-in fade-in-0 zoom-in-95">
      <button
        onClick={onClose}
        className="absolute right-4 top-4 p-1 rounded-full hover:bg-slate-100"
        aria-label="Close dialog"
      >
        <X className="h-5 w-5" />
      </button>
      
      <div className="pt-2 pb-4">
        <h3 className="text-xl font-bold mb-2 flex items-center">
          <div 
            className="w-5 h-5 rounded-md mr-2" 
            style={{ backgroundColor: belt.color, border: belt.color === '#ffffff' ? '1px solid #e5e7eb' : 'none' }}
          ></div>
          {displayTitle}
        </h3>
        
        {curriculum ? (
          <div className="space-y-4">
            <h4 className="text-lg font-medium">{curriculum.title}</h4>
            
            {curriculum.content && (
              <SimpleMarkdown content={curriculum.content} />
            )}
            
            {/* MesoCurriculum specific fields */}
            {'key_techniques' in curriculum && curriculum.key_techniques && curriculum.key_techniques.length > 0 && (
              <div className="mt-4">
                <h5 className="text-md font-medium">Key Techniques:</h5>
                <ul className="list-disc pl-5 mt-2">
                  {curriculum.key_techniques.map((technique, index) => (
                    <li key={index}>{technique}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {'learning_objectives' in curriculum && curriculum.learning_objectives && curriculum.learning_objectives.length > 0 && (
              <div className="mt-4">
                <h5 className="text-md font-medium">Learning Objectives:</h5>
                <ul className="list-disc pl-5 mt-2">
                  {curriculum.learning_objectives.map((objective, index) => (
                    <li key={index}>{objective}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-4 text-gray-500">
            No content available for this belt
          </div>
        )}
      </div>
    </Card>
  );
};
